// External dependencies
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Internal dependencies
import { getPage } from '@/modules/cms/client/api/cms.api';
import Board from '@/modules/core/client/components/Board';
import LoadingIndicator from '@/modules/core/client/components/LoadingIndicator';

/**
 * A public, admin authored content page.
 *
 * The body is server sanitized on both write and read, which is what makes
 * `dangerouslySetInnerHTML` acceptable here — see
 * `modules/cms/server/services/cms-sanitize.server.service.js`.
 */
export default function CmsPage({ slug }) {
  const { t, i18n } = useTranslation('cms');

  const [page, setPage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchPage() {
      setIsLoading(true);
      setNotFound(false);

      try {
        const data = await getPage(slug, { locale: i18n.language });

        if (cancelled) {
          return;
        }

        setPage(data);

        // Deliberately no `history.replaceState` here to rewrite an old slug
        // to the canonical one. The app runs ui-router in html5Mode, so
        // changing the URL out from under it makes `$browser` notice the
        // divergence on the next digest and re-transition this very state —
        // remounting the component and refetching. The old slug keeps
        // resolving through `slugHistory` anyway, so the rewrite bought
        // little and cost a double fetch.
      } catch (err) {
        if (!cancelled) {
          setNotFound(true);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    if (slug) {
      fetchPage();
    }

    // Guard against a slug change resolving out of order.
    return () => {
      cancelled = true;
    };
  }, [slug, i18n.language]);

  useEffect(() => {
    if (page && page.title) {
      document.title = page.metaTitle || page.title;
    }
  }, [page]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (notFound || !page) {
    return (
      <section className="container container-spacer">
        <h2>{t('Page not found')}</h2>
        <p className="lead">
          {t('This page does not exist, or is not published yet.')}
        </p>
      </section>
    );
  }

  return (
    <>
      <Board names="forestpath">
        <div className="container">
          <div className="row">
            <div className="col-xs-12">
              <h2>{page.title}</h2>
            </div>
          </div>
        </div>
      </Board>
      <section className="container container-spacer">
        <div className="row">
          <div className="col-xs-12 col-sm-10 col-sm-offset-1">
            <article
              className="cms-page-body"
              dangerouslySetInnerHTML={{ __html: page.body }}
            />
          </div>
        </div>
      </section>
    </>
  );
}

CmsPage.propTypes = {
  slug: PropTypes.string,
};
